import { api } from './api';

export const REFERRAL_REWARD_CREDITS = 5;
export const REFERRAL_CAP = 10;

/**
 * Session key for a `?ref=CODE` captured from the landing URL before sign-in
 * completes. Kept in sessionStorage (not localStorage) so closing the tab
 * after an abandoned signup doesn't persist the code forever.
 */
const PENDING_REF_KEY = 'gradelab.pending_ref';

/** Normalise a user-entered or URL-sourced code to canonical form. */
export function normaliseCode(raw: string | null | undefined): string {
  return String(raw ?? '').trim().toUpperCase();
}

/** Stash a referral code for later attach-after-signup. */
export function setPendingReferral(code: string): void {
  const clean = normaliseCode(code);
  if (!clean) return;
  try { sessionStorage.setItem(PENDING_REF_KEY, clean); } catch { /* storage may be blocked */ }
}

/** Read the pending code if any. Does NOT clear it — callers decide when. */
export function getPendingReferral(): string | null {
  try {
    const v = sessionStorage.getItem(PENDING_REF_KEY);
    return v ? normaliseCode(v) : null;
  } catch { return null; }
}

/** Clear the pending code once successfully attached (or permanently rejected). */
export function clearPendingReferral(): void {
  try { sessionStorage.removeItem(PENDING_REF_KEY); } catch { /* ignore */ }
}

export interface ReferralState {
  /**
   * Shareable referral code. `null` until the user has verified their own
   * phone number — the backend gates code issuance on phone verification to
   * prevent fake-email signup rings from collecting codes for free.
   */
  code: string | null;
  rewardCredits: number;
  cap: number;
  rewardedCount: number;
  creditsEarned: number;
  referredBy: string | null;
  bonusAwarded: boolean;
  phoneVerified: boolean;
}

/** GET /referrals/me — full picture for the signed-in user. */
export async function getReferralState(): Promise<ReferralState> {
  const { data } = await api.get<ReferralState>('/referrals/me');
  return data;
}

export interface AttachResult {
  ok: boolean;
  alreadyAttached?: boolean;
  referrerUid?: string;
}

/** POST /referrals/attach — bind the current user to a referrer's code. */
export async function attachReferralCode(code: string): Promise<AttachResult> {
  const { data } = await api.post<AttachResult>('/referrals/attach', { code: normaliseCode(code) });
  return data;
}

export interface RedeemResult {
  ok: boolean;
  /** 5 if the user was referred + referrer still had cap remaining; else 0. */
  credited: number;
  /** 5 if the user was referred + referrer still had cap remaining; else 0. */
  referrerCredited: number;
  /** The user's own newly-activated referral code (always set on success). */
  code: string | null;
  /** True if a prior call already verified this account; safe to ignore. */
  alreadyVerified?: boolean;
}

/**
 * POST /referrals/redeem — unified phone-verification endpoint.
 *
 * Called once per account after Firebase Phone Auth linking succeeds:
 *   - organic signups: activates the user's own shareable code (no credits).
 *   - referee signups: activates code AND credits 5 to each side.
 *
 * Idempotent — a repeat call on an already-verified account returns
 * `alreadyVerified: true` with zero deltas.
 */
export async function redeemReferral(): Promise<RedeemResult> {
  const { data } = await api.post<RedeemResult>('/referrals/redeem', {});
  return data;
}

/**
 * Build a share URL that carries the user's code as `?ref=CODE`. The landing
 * page / login page pick that up and route it through the attach flow after
 * sign-in.
 */
export function buildShareUrl(code: string | null | undefined, origin?: string): string {
  const clean = normaliseCode(code);
  if (!clean) return '';  // No code → no URL; caller should hide share UI.
  const base = origin ?? (typeof window !== 'undefined' ? window.location.origin : 'https://gradelab.club');
  const params = new URLSearchParams({ ref: clean });
  return `${base}/?${params.toString()}`;
}
